import { ConversationService } from '@forwardflow/services/ConversationService'
import { TextField } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { InputRef, message } from 'antd'
import Form from 'antd/es/form'
import { debounce } from 'lodash'
import React, { useRef, useState } from 'react'

interface MessageInputProps {
   leadId: number | null
   onOptimisticUpdate: (message: string, isBuyer: boolean) => void
}

export const MessageInput: React.FC<MessageInputProps> = ({
   leadId,
   onOptimisticUpdate,
}) => {
   const [form] = Form.useForm()
   const [isSubmitting, setIsSubmitting] = useState(false)
   const inputRef = useRef<InputRef>(null)

   // Check if we're in demo mode
   const isDemoMode = window.location.pathname.includes('demo-conversations-old')

   const sanitizeMessage = (text: string): string => {
      // Remove HTML tags
      const withoutHtml = text.replace(/<[^>]*>/g, '')
      // Remove common SQL injection patterns
      const withoutSql = withoutHtml.replace(/['";]/g, '')
      // Remove script tags and content
      const withoutScript = withoutSql.replace(
         /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
         ''
      )
      return withoutScript.trim()
   }

   const debouncedSendMessage = debounce(async (values) => {
      if (isSubmitting) return

      setIsSubmitting(true)
      try {
         const sanitizedMessage = sanitizeMessage(values.sendSmsMessage)

         if (leadId && sanitizedMessage) {
            if (!isDemoMode) {
               message.loading('Sending message...')
            }

            if (isDemoMode) {
               await ConversationService.sendDemoMessage(
                  leadId,
                  sanitizedMessage
               )
            } else {
               await ConversationService.sendMessage(leadId, sanitizedMessage)
            }
            
            if (!isDemoMode) {
               message.destroy()
            }
         }
      } catch (error) {
         console.error('Error sending message:', error)
         message.error('Failed to send message')
      } finally {
         setIsSubmitting(false)
      }
   }, 1000)

   const handleSendMessage = async () => {
      const values = await form.validateFields()
      if (values && values.sendSmsMessage?.trim()) {
         const sanitizedMessage = sanitizeMessage(values.sendSmsMessage)
         
         form.resetFields()
         
         if (isDemoMode) {
            onOptimisticUpdate(sanitizedMessage, true)
            debouncedSendMessage(values)
         } else {
            onOptimisticUpdate(sanitizedMessage, false)
            debouncedSendMessage(values)
         }
      }
   }

   return (
      <div className="flex items-center flex-1 px-4 mx-4 mt-1 border rounded-lg bg-white/5 border-white/20 ">
         <Form
            layout="vertical"
            form={form}
            onFinish={handleSendMessage}
            className="flex items-center flex-1 w-full"
            autoComplete="off"
         >
            <div className="flex items-center w-full conversation-input-container">
               <Form.Item
                  name="sendSmsMessage"
                  className="!mb-0 flex-1"
                  rules={[
                     { required: true, message: 'Please enter a message' },
                     { whitespace: true, message: 'Message cannot be empty' },
                     { max: 1600, message: 'Message is too long' },
                     {
                        transform: (value) => {
                           if (value) {
                              return sanitizeMessage(value)
                           }
                           return value
                        },
                     },
                  ]}
               >
                  <TextField
                     ref={inputRef}
                     type="text"
                     className="w-full !pl-0 pr-4 py-3  focus:outline-none !border-none !bg-transparent !outline-none !shadow-none !mb-0"
                     placeholder="Enter your reply to the contact"
                  />
               </Form.Item>

               <button
                  className="pl-8 pr-1 bg-transparent cursor-pointer hover:"
                  type="submit"
                  disabled={isSubmitting}
               >
                  <Icon name="Send" className="scale-[1.25] fill-white" />
               </button>
            </div>
         </Form>
      </div>
   )
}
